import { isImpersonating } from "api/apiService";
import useHelpers from "composables/useHelpers";
import type { AppRouteMeta, AppRouteRecordRaw } from "shared/types";
import { useModuleStore } from "store/modules/moduleStore";
import type {
  Router,
  RouteLocationNormalized,
  RouteLocationRaw,
} from "vue-router";

const { userHasRole } = useHelpers();

let cachedRouter: Router | null = null;

export const registerRouter = (router: Router): void => {
  cachedRouter = router;
};

export const getRouter = (): Router | null => cachedRouter;

export const isRouteAccessible = (
  route: AppRouteRecordRaw | null | undefined,
): boolean => {
  if (!route) {
    return false;
  }

  const meta = route.meta ?? {};

  if (meta.onlyWhenImpersonating === true && !isImpersonating.value) {
    return false;
  }

  if (meta.allowWhenImpersonating === true && isImpersonating.value) {
    return true;
  }

  const { hasSettingEnabled } = useModuleStore();

  if (meta.settingRequired && !meta.settingRequired.some(hasSettingEnabled)) {
    return false;
  }

  if (meta.roleRequired && !userHasRole(meta.roleRequired)) {
    return false;
  }

  return true;
};

/**
 * Access check for a resolved navigation target.
 *
 * `route.meta` is vue-router's merge of every matched record, so the deepest
 * record that declares `roleRequired` decides. A parent's list is only the
 * fallback, never an extra gate on top: that list doubles as the menu gate
 * (menuStore reads the same meta), so treating it as a hard gate locks a role
 * out of every detail page under a menu entry it may not see -- which is what
 * kept a teamleader out of the meetings they open from the meeting overview.
 */
export const isAuthorized = (route: RouteLocationNormalized): boolean => {
  const meta = route.meta as AppRouteMeta;

  if (meta.onlyWhenImpersonating === true && isImpersonating.value === false) {
    return false;
  }

  if (meta.allowWhenImpersonating === true && isImpersonating.value === true) {
    return true;
  }

  const roleRequired = meta.roleRequired;

  if (roleRequired === undefined) {
    return true;
  }

  const { hasSettingEnabled } = useModuleStore();
  const settingRequired = (
    route.matched[route.matched.length - 1]?.meta as AppRouteMeta | undefined
  )?.settingRequired;

  if (settingRequired && settingRequired.some(hasSettingEnabled) === false) {
    return false;
  }

  return userHasRole(roleRequired);
};

export const findRouteByName = (
  routes: readonly AppRouteRecordRaw[],
  name: string,
): AppRouteRecordRaw | null => {
  for (const route of routes) {
    if (route.name === name) {
      return route;
    }

    if (route.children) {
      const found = findRouteByName(route.children, name);

      if (found) {
        return found;
      }
    }
  }

  return null;
};

export const isRouteAccessibleByName = (
  router: Router,
  routeName: string,
): boolean => {
  return isRouteAccessible(
    findRouteByName(
      router.options.routes as readonly AppRouteRecordRaw[],
      routeName,
    ),
  );
};

export const pickFirstAccessibleChild = (
  router: Router,
  parentRouteName: string,
): RouteLocationRaw | null => {
  const parent = findRouteByName(
    router.options.routes as readonly AppRouteRecordRaw[],
    parentRouteName,
  );

  if (!parent?.children?.length) {
    return null;
  }

  for (const child of parent.children) {
    if (isRouteAccessible(child)) {
      return { name: child.name as string };
    }
  }

  return null;
};
